import { ApiFunctions, apiUrl } from "./ApiConfig";
import { EnvHelper } from "../utils/EnvHelper";
import {
  MockupWithImagesViewModel,
  MockupBindingModel,
  MockupSearchModel,
  ApiResponse,
} from "./clientInterfaces/MockupModel";
import {
  CategoryViewModel,
  CategoryBindingModel,
} from "./clientInterfaces/CategoryModel";
import { MockClient } from "./MockClient";
import { UserViewModel } from "./clientInterfaces/UserModel";
import {
  AuthenticationModel,
  AuthenticationModelGoogle,
  AuthenticationResponse,
  RegisterResponse,
  RegisterModel,
} from "./clientInterfaces/AuthenticationModel";
import { getToken, setToken } from "../utils/AuthCookies";

const fetcher = async <T>(url: string): Promise<T> => {
  const resp = await fetch(url);
  return resp.json();
};

const fetcherPost = async <T>(data: any): Promise<T> => {
  const resp = await fetch(data.url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data.body),
  });
  return resp.json();
};

export const fetcherWithCredentials = async <T>(url: string): Promise<T> => {
  const token = getToken();
  try {
    if (token) {
      const resp = await fetch(url, {
        headers: {
          Authorization: token,
        },
      });
      return resp.json();
    }
  } catch (error) {
    throw new Error(error);
  }
};

class _ApiClient implements ApiFunctions {
  private static instance: _ApiClient;

  private constructor() {}

  static getInstance(): _ApiClient {
    if (!_ApiClient.instance) {
      _ApiClient.instance = new _ApiClient();
      return _ApiClient.instance;
    } else {
      return _ApiClient.instance;
    }
  }

  getMockup = async (query: MockupBindingModel) => {
    const result = await fetcher<MockupWithImagesViewModel>(
      apiUrl.getMockup(query.mockupId)
    );
    return result;
  };

  getRelatedMockups = async (query: MockupBindingModel) => {
    const result = await fetcher<MockupWithImagesViewModel[]>(
      apiUrl.getRelatedMockups(query.mockupId)
    );
    return result;
  };

  getCategories = async () => {
    const result = await fetcher<CategoryViewModel[]>(apiUrl.getCategories);
    return result;
  };

  getCategoryMockups = async (query: CategoryBindingModel) => {
    const result = await fetcher<MockupWithImagesViewModel[]>(
      apiUrl.getCategoryMockups(
        query.categoryId,
        query.tags,
        query.limit,
        query.page
      )
    );
    return result;
  };

  getUserInfo = async () => {
    try {
      const result = await fetcherWithCredentials<UserViewModel>(
        apiUrl.getUserInfo
      );
      return result;
    } catch (error) {
      throw new Error(error);
    }
  };

  getDefaultMockups = async () => {
    const { results } = await fetcher<ApiResponse>(apiUrl.getDefaultMockups);
    return results;
  };

  login = async (query: AuthenticationModel) => {
    const { username, password, captchaToken } = query;
    if (!username || !password || !captchaToken) {
      return { error: true };
    }
    const res = await fetcherPost<AuthenticationResponse>({
      url: apiUrl.login,
      method: "POST",
      body: query,
    });
    const { accessToken } = res;
    setToken(accessToken, "local")
    return res;
  };

  loginGoogle = async (token: AuthenticationModelGoogle) => {
    const res = await fetcherPost<any>({
      url: apiUrl.loginGoogle,
      method: "POST",
      body: {
        id_token: token,
      },
    });
    const { accessToken } = res;
    setToken(accessToken, "google")
    return res;
  };

  loginFacebook = async (token: string) => {
    const res = await fetcherPost<any>({
      url: apiUrl.loginFacebook,
      method: "POST",
      body: {
        accessToken: token,
      },
    });
    const { accessToken } = res;
    setToken(accessToken, "facebook")
    return res;
  };

  signUp = async (query: RegisterModel) => {
    const { error, message, data } = await fetcherPost<RegisterResponse>({
      url: apiUrl.signUp,
      method: "POST",
      body: query,
    });
    return {
      error,
      message,
      data,
    };
  };

  getCommonTranslations = async () => {
    const results = await fetcher<ApiResponse>(apiUrl.getCommonTranslations);
    return results;
  };

  getMockupSearch = async (query: MockupSearchModel) => {
    const res = await fetcher<ApiResponse>(
      apiUrl.getMockupSearch(query.mockup, query.page)
    );
    return res;
  };
}

export const ApiClient = EnvHelper.apiMock
  ? MockClient.getInstance()
  : _ApiClient.getInstance();
